import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import {
  Typography,
  Box,
  CssBaseline,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
} from '@mui/material';
import { Phone, PauseCircle, PlayArrow, Download } from '@mui/icons-material';
import axios from 'axios';
import { global_link } from '../Components/Globallink';
import WebSocketContext from '../Context/WebSocket';

const Calls = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [calltypeFilter, setCalltypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentRecordingId, setCurrentRecordingId] = useState(null);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const api = axios.create({ baseURL: global_link });
  const authToken = process.env.AUTH_TOKEN;

  // ── Use shared context socket — no throwaway socket ───────────────────────
  const { socket } = useContext(WebSocketContext);

  // ── fetchData ref — always points to latest version with current params ───
  const fetchDataRef = useRef(null);

  // Determine if current user is a supervisor (CSV download visible only to supervisors/admins)
  useEffect(() => {
    const userLevel = sessionStorage.getItem('user_level');
    setIsSupervisor(userLevel === 'supervisor' || userLevel === 'admin');
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const authyToken = localStorage.getItem(authToken);
      const response = await api.get('/call/getcalllogs', {
        params: {
          search: searchTerm,
          call_type: calltypeFilter,
          start_date: startDate,
          end_date: endDate,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        },
        headers: { Authorization: `Bearer ${authyToken}` },
      });
      setData(response.data.data);
      setTotalItems(response.data.total);
    } catch (error) {
      console.error('Error fetching call logs:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, calltypeFilter, startDate, endDate, page, rowsPerPage, authToken]);

  // Keep ref in sync with latest fetchData every time params change
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // Run fetchData when params change
  useEffect(() => {
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      console.error('End date cannot be earlier than start date.');
      return;
    }
    fetchData();
  }, [fetchData]);

  // ── Socket: NEW_CALL_LOG_UPDATE ───────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    const handleCallLogUpdate = () => {
      fetchDataRef.current?.();
    };

    socket.on('connect', handleCallLogUpdate);
    socket.on('NEW_CALL_LOG_UPDATE', handleCallLogUpdate);

    return () => {
      socket.off('NEW_CALL_LOG_UPDATE', handleCallLogUpdate);
      socket.off('connect', handleCallLogUpdate);
    };
  }, [socket]);

  // ── Window event from DialBar (same-tab call end) ─────────────────────────
  useEffect(() => {
    const handleUpdateCallsData = () => fetchDataRef.current?.();
    window.addEventListener('updateCallsDataEvent', handleUpdateCallsData);
    return () => window.removeEventListener('updateCallsDataEvent', handleUpdateCallsData);
  }, []);

  // ── Audio playback ────────────────────────────────────────────────────────
  const handleAudioEnd = useCallback(() => {
    setActiveId(null);
    setCurrentAudio(null);
    setCurrentRecordingId(null);
  }, []);

  const togglePlay = useCallback((recordingId) => {
    if (currentAudio && !currentAudio.paused) {
      if (currentRecordingId === recordingId) {
        currentAudio.pause();
        setActiveId(null);
        return;
      } else {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        setActiveId(null);
      }
    }

    if (currentRecordingId === recordingId && currentAudio && currentAudio.paused) {
      currentAudio.play()
        .then(() => setActiveId(recordingId))
        .catch(console.error);
      return;
    }

    if (currentAudio) {
      currentAudio.removeEventListener('ended', handleAudioEnd);
    }

    const audio = new Audio(`${global_link}/uploads/recordings/${recordingId}.webm`);
    setCurrentAudio(audio);
    setCurrentRecordingId(recordingId);

    audio.addEventListener('ended', handleAudioEnd);
    audio.play()
      .then(() => setActiveId(recordingId))
      .catch(console.error);
  }, [currentAudio, currentRecordingId, handleAudioEnd]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.removeEventListener('ended', handleAudioEnd);
      }
    };
  }, [currentAudio, handleAudioEnd]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function maskNumber(num) {
    if (!num) return "";
    const str = String(num);
    if (str.length <= 5) return str;
    const start = str.slice(0, 2);
    const end = str.slice(-2);
    const hidden = "*".repeat(Math.max(3, str.length - 4));
    return start + hidden + end;
  }

  const handleDownload = async (call_id, call_from, call_to, recordingId) => {
    const maskedFrom = maskNumber(call_from);
    const maskedTo = maskNumber(call_to);
    const audioUrl = `${global_link}/uploads/recordings/${recordingId}.webm`;
    try {
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `recording_${maskedFrom} - ${maskedTo}_${call_id}.webm`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download the file. Please try again.");
    }
  };

  const copyPhoneNumber = (call_from, call_to) => {
    const phoneNumber = call_from.length > 5 ? call_from : call_to;
    window.dispatchEvent(new CustomEvent('copyPhoneNumber', { detail: phoneNumber }));
  };

  const getDurationColor = (duration) => {
    if (duration === 'Missed') return '#ff5722';
    if (duration === 'Busy') return '#ff9800';
    if (duration === 'Rejected') return '#f44336';
    if (duration === 'No Answer') return '#9e9e9e';
    if (duration === 'Failed') return '#b71c1c';
    if (duration === 'Invalid Number') return '#b71c1c';
    if (duration === 'Timeout') return '#9e9e9e';
    if (duration === 'Flashed') return '#ff5722';
    if (duration === 'Forbidden') return '#b71c1c';
    return 'inherit';
  };

  // Durations that never have a real recording — no play/download buttons shown
  const NON_RECORDED_DURATIONS = [
    'Busy', 'Missed', 'No Answer', 'Rejected',
    'Failed', 'Invalid Number', 'Timeout', 'Forbidden', 'Canceled', 'Flashed',
  ];

  const hasRecording = (row) =>
    sessionStorage.getItem('user_level') !== 'agent' &&
    row.recording_id &&
    !NON_RECORDED_DURATIONS.includes(row.call_duration);

  // Escape a CSV cell value
  const escapeCsvCell = (value) => {
    const str = (value ?? '').toString().replace(/"/g, '""');
    return `"${str}"`;
  };

  // Download all matching searched/filtered call logs as CSV (supervisor only)
  const downloadCSV = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const authyToken = localStorage.getItem(authToken);
      const response = await api.get('/call/getcalllogs', {
        params: {
          search: searchTerm,
          call_type: calltypeFilter,
          start_date: startDate,
          end_date: endDate,
          limit: 10000,
          offset: 0,
        },
        headers: { Authorization: `Bearer ${authyToken}` },
      });
      const exportData = response.data?.data || [];
      if (!exportData.length) {
        alert('No matching records found to download.');
        return;
      }

      const headers = [
        'Name',
        'Source',
        'Destination',
        'Duration',
        'Type',
        'Date',
      ];
      const rows = exportData.map((row) => [
        escapeCsvCell(row.user_name),
        escapeCsvCell(row.call_from),
        escapeCsvCell(row.call_to),
        escapeCsvCell(row.call_duration),
        escapeCsvCell(row.call_type),
        escapeCsvCell(row.call_time),
      ]);

      const csvContent =
        headers.map(escapeCsvCell).join(',') +
        '\n' +
        rows.map((r) => r.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.setAttribute('download', `call_logs_export_${dateStamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <CssBaseline />
      <Typography variant="h5" sx={{ marginBottom: '15px' }}>
        Call Log
      </Typography>
      <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid rgba(207, 211, 212, 0.2)`, borderRadius: '5px' }}>
        <Box sx={{ padding: 2, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' } }}>
          <TextField
            label="Search"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search"
          />
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Filter</InputLabel>
            <Select value={calltypeFilter} onChange={e => setCalltypeFilter(e.target.value)} label="Filter by Role">
              <MenuItem value="">Call Type</MenuItem>
              <MenuItem value="Incoming">Incoming</MenuItem>
              <MenuItem value="Outgoing">Outgoing</MenuItem>
              <MenuItem value="Received">Answered</MenuItem>
              <MenuItem value="Missed">Missed</MenuItem>
              <MenuItem value="Flashed">Flashed</MenuItem>
              <MenuItem value="No Answer">No Answer</MenuItem>
              <MenuItem value="Busy">Busy</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
              <MenuItem value="Failed">Failed</MenuItem>
              <MenuItem value="Timeout">Timeout</MenuItem>
              <MenuItem value="Invalid Number">Invalid Number</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          {/* CSV Download - visible only to supervisors/admins */}
          {isSupervisor && (
            <Button
              variant="contained"
              color="success"
              sx={{ padding: '10px', minWidth: 'auto', whiteSpace: 'nowrap' }}
              onClick={downloadCSV}
              disabled={isDownloading}
              startIcon={<Download />}
            >
              {isDownloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          )}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.table', width: '100%' }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Destination</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Loading...</TableCell>
                </TableRow>
              ) : data.length > 0 ? (
                data.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.user_name}</TableCell>
                    <TableCell>{row.call_from}</TableCell>
                    <TableCell>{row.call_to}</TableCell>
                    <TableCell sx={{ color: `${getDurationColor(row.call_duration)} !important` }}>
                      {row.call_duration}
                    </TableCell>
                    <TableCell>{row.call_type}</TableCell>
                    <TableCell>{row.call_time}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="info"
                        sx={{ padding: '1px', margin: '5px' }}
                        onClick={() => copyPhoneNumber(row.call_from, row.call_to)}
                      >
                        <Phone sx={{ fontSize: '1.5rem', cursor: 'pointer', margin: '5px' }} />
                      </Button>
                      {hasRecording(row) && (
                        <>
                          <Button
                            variant="contained"
                            color="warning"
                            sx={{ padding: '6px', margin: '5px' }}
                            onClick={() => togglePlay(`call_${row.recording_id}`)}
                          >
                            {activeId === `call_${row.recording_id}` ? (
                              <PauseCircle sx={{ fontSize: '1.5rem', cursor: 'pointer' }} />
                            ) : (
                              <PlayArrow sx={{ fontSize: '1.5rem', cursor: 'pointer' }} />
                            )}
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            sx={{ padding: '6px', margin: '5px' }}
                            onClick={() => handleDownload(row.call_id, row.call_from, row.call_to, `call_${row.recording_id}`)}
                          >
                            <Download />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">No matching records found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 50, 100]}
          labelRowsPerPage="Rows per page"
        />
      </Box>
    </>
  );
};

export default Calls;